import { DataType, TableName } from '../../../tables'
import { DynamoUserApi } from '../../../services'
import { define } from '../../../utils/typeUtils'
import { useSystemStateHooks } from '../../system'
import { useStateHooks } from '../../../state'
import { useDefaultDbActions } from './defaultDbActions'
import { DbWriteOperation } from '../types'

export const useSaveActions = () => {
    const { companyId: stateCompanyId, userId } = useSystemStateHooks()
    const { getUpdater, getRemover, find } = useStateHooks()

    const withNegativeStateAudit = (table: TableName, item: DataType): DataType => {
        const anyItem = item as any
        const isNegative = anyItem.archived === true || anyItem.cancelled === true || anyItem.status === 'Annulled'
        if (!isNegative || anyItem.negativeStateUserId) {
            return item
        }
        // Los builders no copian estos campos: si el registro ya estaba archivado/cancelado se conserva quién y cuándo lo hizo
        let previous: any
        try {
            previous = find(table, anyItem.id)
        } catch {
            previous = undefined
        }
        if (previous?.negativeStateUserId) {
            return { ...anyItem, negativeStateUserId: previous.negativeStateUserId, negativeStateAt: previous.negativeStateAt }
        }
        if (userId) {
            return { ...anyItem, negativeStateUserId: userId, negativeStateAt: new Date().toISOString() }
        }
        return item
    }

    const defaultDbActions = useDefaultDbActions()

    const updater = (table: TableName) => getUpdater(table) as any as (list: DataType[]) => void
    const remover = (table: TableName) => getRemover(table) as any as (ids: string[]) => void

    return (paramCompanyId?: string) => {
        const companyId = paramCompanyId || stateCompanyId
        const dbActions = defaultDbActions(DynamoUserApi, define(companyId))

        const save = async (
            table: TableName,
            dataType: DataType
        ) => {
            const enriched = withNegativeStateAudit(table, dataType)
            const list = [enriched]
            await dbActions.save(table, list)
            updater(table)(list)
            return enriched
        }

        const saveMultiple = async (
            table: TableName,
            list: DataType[]
        ) => {
            const enrichedList = list.map(item => withNegativeStateAudit(table, item))
            await dbActions.save(table, enrichedList)
            updater(table)(enrichedList)
            return enrichedList
        }

        const remove = async (
            table: TableName,
            id: string
        ) => {
            const ids = [id]
            await dbActions.remove(table, ids)
            remover(table)(ids)
        }

        const removeMultiple = async (
            table: TableName,
            ids: string[]
        ) => {
            await dbActions.remove(table, ids)
            remover(table)(ids)
        }

        // Applies all the operations or none of them, and then syncs the state with the result
        const transactWrite = async (operations: DbWriteOperation[]) => {
            await dbActions.transactWrite(operations)

            for (const operation of operations) {
                if (operation.type === 'put') {
                    updater(operation.table)([operation.dataType])
                } else if (operation.type === 'delete') {
                    remover(operation.table)([operation.id])
                } else {
                    // Incremented values are computed by the db, so the item is reloaded
                    const item = await dbActions.get(operation.table, operation.id)
                    item && updater(operation.table)([item])
                }
            }
        }

        return {
            save,
            saveMultiple,
            remove,
            removeMultiple,
            transactWrite
        }
    }
}
